import type { LocalRow, SeedTables } from '../types/localDb.types'
import { roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Konfigurasi Workflow Studio WF-PRC-01 (Purchase Request) — disalin dari
 * `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-PRC-01.md` §1–§8 (label [C]).
 * Tabel email, tabbing, dan SLA tidak di-seed karena tidak dijalankan local mode.
 */

const WORKFLOW_NO = 1
export const WF_PRC_01_ID = seedId(SEED_GROUP.workflow, WORKFLOW_NO)

const scoped = (n: number) => WORKFLOW_NO * 100 + n

const STATUSES = [
  ['PRC_DRAFT', 'Draft', 1, true, false, '#64748B', 'PR baru dibuat oleh Admin RM'],
  [
    'PRC_PENDING_SPV',
    'Pending Approval - Supervisor',
    2,
    false,
    false,
    '#F59E0B',
    'Menunggu persetujuan Supervisor RM'
  ],
  ['PRC_PENDING_PURCH', 'Pending Approval - Purchasing', 3, false, false, '#3B82F6', 'Menunggu persetujuan Purchasing'],
  ['PRC_APPROVED', 'Approved', 4, false, true, '#10B981', 'PR disetujui penuh'],
  [
    'PRC_REJECTED_SPV',
    'Rejected - by Supervisor',
    5,
    false,
    false,
    '#EF4444',
    'Ditolak Supervisor, dapat diedit ulang'
  ],
  [
    'PRC_REJECTED_PURCH',
    'Rejected - by Purchasing',
    6,
    false,
    false,
    '#EF4444',
    'Ditolak Purchasing, dapat diedit ulang'
  ]
] as const

export type PrcStatusCode = (typeof STATUSES)[number][0]

export const prcStatusId = (code: PrcStatusCode) =>
  seedId(SEED_GROUP.wmlStatus, scoped(STATUSES.findIndex(([c]) => c === code) + 1))

const ACTORS = [
  ['ACT_PRC_ADMIN_RM', 'Admin WH RM', 'admin_wh_rm', { created_by: { _eq: '$CURRENT_USER.id' } }],
  /** D3 (keputusan user 2026-10-28): Supervisor juga menyetujui PR buatannya sendiri — tanpa kondisi `created_by`. */
  ['ACT_PRC_SPV_RM', 'WH RM Supervisor', 'wh_rm_supervisor', null],
  ['ACT_PRC_PURCHASING', 'Purchasing Officer', 'purchasing', null],
  ['ACT_PRC_KADIV_RM', 'Kadiv WH RM', 'kadiv_wh_rm', null],
  /** D3 (keputusan user 2026-10-28): Supervisor juga membuat PR dari papan ROP. */
  [
    'ACT_PRC_SPV_CREATOR',
    'WH RM Supervisor (pembuat PR)',
    'wh_rm_supervisor',
    { created_by: { _eq: '$CURRENT_USER.id' } }
  ]
] as const

export const prcActorId = (code: (typeof ACTORS)[number][0]) =>
  seedId(SEED_GROUP.wmlActor, scoped(ACTORS.findIndex(([c]) => c === code) + 1))

/** ui_config: TL 12B §5 hanya mendefinisikan CMP_PRC_SUBMIT; warna komponen lain [A] mengikuti pola yang sama. */
const COMPONENTS = [
  ['CMP_PRC_SUBMIT', 'Tombol Ajukan PR', 'ACTION', { color: '#3B82F6', variant: 'primary', icon: 'tabler:send' }],
  ['CMP_PRC_APPROVE_SPV', 'Tombol Approve Supervisor', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_PRC_REJECT_SPV', 'Tombol Reject Supervisor', 'ACTION', { color: '#EF4444', icon: 'tabler:x' }],
  ['CMP_PRC_APPROVE_PURCH', 'Tombol Approve Purchasing', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_PRC_REJECT_PURCH', 'Tombol Reject Purchasing', 'ACTION', { color: '#EF4444', icon: 'tabler:x' }],
  ['CMP_PRC_REVISE', 'Tombol Edit & Ajukan Ulang', 'ACTION', { color: '#3B82F6', icon: 'tabler:send' }],
  ['CMP_PRC_PANEL_HEADER', 'Panel Header PR', 'PANEL', {}],
  ['CMP_PRC_PANEL_ITEMS', 'Panel Item Lines PR', 'PANEL', {}],
  ['CMP_PRC_PANEL_HISTORY', 'Panel History & Audit Trail', 'PANEL', {}]
] as const

type ComponentCode = (typeof COMPONENTS)[number][0]

const componentId = (code: ComponentCode) =>
  seedId(SEED_GROUP.wmlComponent, scoped(COMPONENTS.findIndex(([c]) => c === code) + 1))

/** [name, from, to, component, is_need_reason, is_reason_required, entity_patch_mapping]. */
const ACTIONS: [
  string,
  PrcStatusCode,
  PrcStatusCode,
  ComponentCode,
  boolean,
  boolean,
  Record<string, unknown> | null
][] = [
  ['ACT_PRC_SUBMIT', 'PRC_DRAFT', 'PRC_PENDING_SPV', 'CMP_PRC_SUBMIT', false, false, null],
  ['ACT_PRC_APP_SPV', 'PRC_PENDING_SPV', 'PRC_PENDING_PURCH', 'CMP_PRC_APPROVE_SPV', false, false, null],
  ['ACT_PRC_REJ_SPV', 'PRC_PENDING_SPV', 'PRC_REJECTED_SPV', 'CMP_PRC_REJECT_SPV', true, true, null],
  [
    'ACT_PRC_APP_PURCH',
    'PRC_PENDING_PURCH',
    'PRC_APPROVED',
    'CMP_PRC_APPROVE_PURCH',
    false,
    false,
    { is_approved: true, approved_date: '$NOW' }
  ],
  ['ACT_PRC_REJ_PURCH', 'PRC_PENDING_PURCH', 'PRC_REJECTED_PURCH', 'CMP_PRC_REJECT_PURCH', true, true, null],
  ['ACT_PRC_RESUBMIT_SPV', 'PRC_REJECTED_SPV', 'PRC_PENDING_SPV', 'CMP_PRC_REVISE', false, false, null],
  ['ACT_PRC_RESUBMIT_PURCH', 'PRC_REJECTED_PURCH', 'PRC_PENDING_SPV', 'CMP_PRC_REVISE', false, false, null]
]

export const prcActionId = (name: string) =>
  seedId(SEED_GROUP.wmlAction, scoped(ACTIONS.findIndex(([n]) => n === name) + 1))

const ELIGIBILITY: [string, (typeof ACTORS)[number][0]][] = [
  ['ACT_PRC_SUBMIT', 'ACT_PRC_ADMIN_RM'],
  ['ACT_PRC_APP_SPV', 'ACT_PRC_SPV_RM'],
  ['ACT_PRC_REJ_SPV', 'ACT_PRC_SPV_RM'],
  ['ACT_PRC_APP_PURCH', 'ACT_PRC_PURCHASING'],
  ['ACT_PRC_REJ_PURCH', 'ACT_PRC_PURCHASING'],
  ['ACT_PRC_RESUBMIT_SPV', 'ACT_PRC_ADMIN_RM'],
  ['ACT_PRC_RESUBMIT_PURCH', 'ACT_PRC_ADMIN_RM'],
  ['ACT_PRC_SUBMIT', 'ACT_PRC_SPV_CREATOR'],
  ['ACT_PRC_RESUBMIT_SPV', 'ACT_PRC_SPV_CREATOR'],
  ['ACT_PRC_RESUBMIT_PURCH', 'ACT_PRC_SPV_CREATOR']
]

/**
 * [komponen panel, status, permission, actor]. TL 12B §8 tidak menyebut actor untuk status REJECTED/APPROVED
 * dan panel HISTORY — [A] write saat REJECTED hanya Admin WH RM (pembuat), read diberikan ke semua actor.
 */
type PrcActorCode = (typeof ACTORS)[number][0]
/** Panel tulis milik pembuat PR: Admin WH RM dan Supervisor pembuat (D3). Urutan = offset id eligibility. */
const CREATORS: PrcActorCode[] = ['ACT_PRC_ADMIN_RM', 'ACT_PRC_SPV_CREATOR']

const PANELS: [ComponentCode, PrcStatusCode, 'read' | 'write', PrcActorCode | PrcActorCode[] | null][] = [
  ['CMP_PRC_PANEL_HEADER', 'PRC_DRAFT', 'write', CREATORS],
  ['CMP_PRC_PANEL_HEADER', 'PRC_PENDING_SPV', 'read', 'ACT_PRC_SPV_RM'],
  ['CMP_PRC_PANEL_HEADER', 'PRC_PENDING_PURCH', 'read', 'ACT_PRC_PURCHASING'],
  ['CMP_PRC_PANEL_HEADER', 'PRC_APPROVED', 'read', null],
  ['CMP_PRC_PANEL_HEADER', 'PRC_REJECTED_SPV', 'write', CREATORS],
  ['CMP_PRC_PANEL_HEADER', 'PRC_REJECTED_PURCH', 'write', CREATORS],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_DRAFT', 'write', CREATORS],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_PENDING_SPV', 'read', 'ACT_PRC_SPV_RM'],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_PENDING_PURCH', 'read', 'ACT_PRC_PURCHASING'],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_APPROVED', 'read', null],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_REJECTED_SPV', 'write', CREATORS],
  ['CMP_PRC_PANEL_ITEMS', 'PRC_REJECTED_PURCH', 'write', CREATORS],
  ['CMP_PRC_PANEL_HISTORY', 'PRC_PENDING_SPV', 'read', null],
  ['CMP_PRC_PANEL_HISTORY', 'PRC_PENDING_PURCH', 'read', null],
  ['CMP_PRC_PANEL_HISTORY', 'PRC_APPROVED', 'read', null]
]

export function buildWorkflowPrcSeed(): SeedTables {
  const panels: LocalRow[] = PANELS.map(([component, status, permission], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
    component_id: componentId(component),
    status_id: prcStatusId(status),
    permission
  }))

  return {
    wml_workflow: [
      {
        ...seedAudit,
        id: WF_PRC_01_ID,
        code: 'WF-PRC-01',
        name: 'Purchase Request Workflow',
        entity: 'purchase_request',
        source_policy: 'manual_create'
      }
    ],
    wml_status: STATUSES.map(([code, name, order, isStart, isTerminal, color, desc], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
      workflow_id: WF_PRC_01_ID,
      code,
      name,
      order,
      is_start: isStart,
      is_terminal: isTerminal,
      is_send_email: false,
      color,
      desc
    })),
    wml_actor: ACTORS.map(([code, name, role, condition]) => ({
      ...seedAudit,
      id: prcActorId(code),
      workflow_id: WF_PRC_01_ID,
      code,
      name,
      role_id: roleId(role),
      job_fn_id: null,
      condition
    })),
    wml_component: COMPONENTS.map(([code, name, type, uiConfig]) => ({
      ...seedAudit,
      id: componentId(code),
      workflow_id: WF_PRC_01_ID,
      code,
      name,
      type,
      ui_config: uiConfig
    })),
    wml_action: ACTIONS.map(([name, from, to, component, needReason, reasonRequired, patch]) => ({
      ...seedAudit,
      id: prcActionId(name),
      workflow_id: WF_PRC_01_ID,
      name,
      from_status_id: prcStatusId(from),
      to_status_id: prcStatusId(to),
      component_id: componentId(component),
      is_need_reason: needReason,
      is_reason_required: reasonRequired,
      is_post_closure: false,
      entity_patch_mapping: patch
    })),
    wml_action_actor_eligibility: ELIGIBILITY.map(([action, actor], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
      action_id: prcActionId(action),
      actor_id: prcActorId(actor)
    })),
    wml_panel: panels,
    /** Panel tanpa actor eksplisit di TL 12B diberi semua actor PR (read-only/ write sesuai `permission`). */
    wml_panel_actor_eligibility: PANELS.flatMap(([, , , actor], index) =>
      (actor ? [actor].flat() : ACTORS.map(([code]) => code)).map((actorCode, offset) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
        panel_id: panels[index].id,
        actor_id: prcActorId(actorCode)
      }))
    )
  }
}
